import { observer } from 'mobx-react-lite';
import React, { useEffect } from 'react';
import { RootStoreContext } from '../../global/storeContext';
import { IDTransactionLog, TransactionStatus, TrxLogType } from 'realhaus-sdk';
import { ButtonBase, Grid, Theme, Typography, createStyles, makeStyles } from '@material-ui/core';
import PaymentIcon from '@mui/icons-material/Payment';
import WithdrawIcon from '@mui/icons-material/Paid';
import TransferIcon from '@mui/icons-material/SwapHoriz';

import StringUtils from '../../utils/string';
import {
    Activity,
    describeTransaction,
    formatSignedAmount,
    STATUS_TONE_COLORS,
} from '../../utils/transaction';
import { TransactionDetailsDrawer } from '../wallet/transactionDetails';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        trxIcon: {
            display: 'block',
            [theme.breakpoints.down(938)]: {
                display: 'none',
            },
        },
        row: {
            display: 'block',
            width: '100%',
            textAlign: 'left',
            borderRadius: theme.shape.borderRadius,
        },
    }),
);

export const RecentTrxActivities: React.FC = observer(() => {
    const { walletStore, userStore } = React.useContext(RootStoreContext);
    const [transactions, setTransactions] = React.useState<IDTransactionLog[]>([]);
    const [selectedActivity, setSelectedActivity] = React.useState<Activity>();

    useEffect(() => {
        const init = async () => {
            let trnx: IDTransactionLog[] = [];
            let lastDoc: any = null;

            while (trnx.length < 5) {
                const activities = await walletStore.getTrxActivities(lastDoc);
                if (!activities || activities.transactions.length === 0) {
                    break;
                }

                lastDoc = activities.lastDoc;
                trnx.push(...activities.transactions);
            }

            trnx = trnx.sort((a, b) => b.timestamp - a.timestamp).slice(0, 5);
            setTransactions(trnx);
        };
        init();
    }, []);

    const classes = useStyles();
    const TrnxIcon = ({ trnx }: { trnx: IDTransactionLog }) => {
        if (trnx.type === TrxLogType.PAYMENT) {
            return <PaymentIcon color='primary' />;
        } else if (trnx.type === TrxLogType.WITHDRAW) {
            return <WithdrawIcon color='primary' />;
        } else {
            return <TransferIcon color='primary' />;
        }
    };

    return (
        <>
            {transactions.map((trnx) => {
                const description = describeTransaction({ trx: trnx }, userStore.userId ?? '');
                return (
                    <ButtonBase
                        key={trnx.id}
                        component='div'
                        className={classes.row}
                        onClick={() => setSelectedActivity({ trx: trnx })}
                    >
                        <Grid container spacing={2} alignItems='center' alignContent='center'>
                            <Grid item xs={1} md={2} className={classes.trxIcon}>
                                <TrnxIcon trnx={trnx} />
                            </Grid>
                            <Grid item xs={7} md={6}>
                                <Typography variant='body2'>
                                    {StringUtils.getDateTimeFormat(trnx.timestamp)}
                                </Typography>
                                <Typography variant='body1'>{description.title}</Typography>
                            </Grid>
                            <Grid item xs={4} md={4}>
                                <Typography variant='body1' align='right'>
                                    <b>
                                        {formatSignedAmount(
                                            description.amount,
                                            description.direction,
                                        )}
                                    </b>
                                </Typography>
                                {trnx.status !== TransactionStatus.SUCCESS && (
                                    <Typography
                                        variant='body2'
                                        align='right'
                                        style={{ color: STATUS_TONE_COLORS[description.status.tone] }}
                                    >
                                        {description.status.label}
                                    </Typography>
                                )}
                            </Grid>
                        </Grid>
                    </ButtonBase>
                );
            })}

            <TransactionDetailsDrawer
                activity={selectedActivity}
                onClose={() => setSelectedActivity(undefined)}
            />
        </>
    );
});
